import { Plugin } from 'ckeditor5/src/core';
import { Typing } from 'ckeditor5/src/typing';
import { createDropdown } from 'ckeditor5/src/ui';
import { CKEditorError } from 'ckeditor5/src/utils';
import EmojisNavigationView from './ui/emojisnavigationview';
import EmojiGridView from './ui/emojigridview';
import EmojiInfoView from './ui/emojiinfoview';
import EmojisView from './ui/emojisview';
import EmojisList from './emojislist';
import emojiIcon from './theme/icons/emoji.svg';
import './theme/emojis.css';
const ALL_EMOJI_GROUP = 'All';
export default class Emojis extends Plugin {
	/**
     * @inheritDoc
     */
    static get requires() {
        return [Typing, EmojisList];
    }
	/**
     * @inheritDoc
     */
	static get pluginName() {
		return 'Emojis';
	}
	/**
     * @inheritDoc
     */
	constructor(editor) {
        super(editor);
        const t = editor.t;
        this._emojis = new Map();
        this._groups = new Map();
        this._allEmojisGroupLabel = t('All');
    }
	/**
     * @inheritDoc
     */
	init() {
		const editor = this.editor;
		const t = editor.t;
		const inputCommand = editor.commands.get('insertText');

		// Add the "emojisButton" to feature components.
		editor.ui.componentFactory.add( 'emojisButton', locale => {
			const dropdownView = createDropdown(locale);
            let dropdownPanelContent;
			dropdownView.buttonView.set({
                label: t('Add Emoji'),
                icon: emojiIcon,
                tooltip: true
            });
			dropdownView.bind('isEnabled').to(inputCommand);
			// Insert a emoji when a tile was clicked.
			dropdownView.on('execute', (evt, data) => {
				editor.execute('insertText', { text: data.emoji });
				editor.editing.view.focus();
			});
			dropdownView.on('change:isOpen', () => {
                if (!dropdownPanelContent) {
                    dropdownPanelContent = this._createDropdownPanelContent(locale, dropdownView);
                    const emojisView = new EmojisView(locale, dropdownPanelContent.navigationView, dropdownPanelContent.gridView, dropdownPanelContent.infoView);
                    dropdownView.panelView.children.add(emojisView);
                }
                dropdownPanelContent.infoView.set({
                    emoji: null,
                    name: null
                });
            });
			return dropdownView;
		} );
	}
	/**
     * Adds a collection of emojis to the specified group. The title of a emoji must be unique.
     *
     * **Note:** The "All" category name is reserved by the plugin and cannot be used as a new name for a
     * emoji category.
     */
    addItems(groupName, items, options = { label: groupName }) {
        if (groupName === ALL_EMOJI_GROUP) {
            /**
             * The name "All" for a emoji category group cannot be used because it is a emoji category that displays all
             * available emojis.
             *
             * @error emojis-invalid-group-name
             */
            throw new CKEditorError('emojis-invalid-group-name', null);
        }
        const group = this._getGroup(groupName, options.label);
        for (const item of items) {
            group.items.add(item.title);
            this._emojis.set(item.title, item.emoji);
        }
    }
    /**
     * Returns emoji groups in an order determined based on configuration and registration sequence.
     */
    getGroups() {
        const groups = Array.from(this._groups.keys());
        const order = this.editor.config.get('Emojis.order') || [];
        const invalidGroup = order.find(item => !groups.includes(item));
        if (invalidGroup) {
            /**
             * One of the emoji groups in the "Emojis.order" configuration doesn't exist.
             *
             * @error emojis-invalid-order-group-name
             */
            throw new CKEditorError('emojis-invalid-order-group-name', null, { invalidGroup });
        }
        return new Set([
            ...order,
            ...groups
        ]);
    }
    /**
     * Returns a collection of emojis symbol names (titles).
     */
    getEmojisForGroup(groupName) {
        const group = this._groups.get(groupName);
        if (group) {
            return group.items;
        }
    }
    /**
     * Returns the symbol of a emoji for the specified name. If the emoji could not be found, `undefined`
     * is returned.
     *
     * @param title The title of a emoji.
     */
    getEmoji(title) {
        return this._emojis.get(title);
    }
    /**
     * Returns a group of emojis. If the group with the specified name does not exist, it will be created.
     *
     * @param groupName The name of the group to create.
     * @param label The label describing the new group.
     */
    _getGroup(groupName, label) {
        if (!this._groups.has(groupName)) {
            this._groups.set(groupName, {
                items: new Set(),
                label
            });
        }
        return this._groups.get(groupName);
    }
    /**
     * Updates the symbol grid depending on the currently selected emoji group.
     */
    _updateGrid(currentGroupName, gridView) {
        // Updating the grid starts with removing all tiles belonging to the old group.
        gridView.tiles.clear();
        const emojiTitles = this.getEmojisForGroup(currentGroupName);
        for (const title of emojiTitles) {
            const emoji = this.getEmoji(title);
            gridView.tiles.add(gridView.createTile(emoji, title));
        }
    }
    /**
     * Initializes the dropdown, used for lazy loading.
     *
     * @returns An object with `navigationView`, `gridView` and `infoView` properties, containing UI parts.
     */
    _createDropdownPanelContent(locale, dropdownView) {
        const groupEntries = Array
            .from(this.getGroups())
            .map(name => ([name, this._groups.get(name).label]));
        // The map contains a name of category (an identifier) and its label (a translational string).
        const emojisGroups = new Map([
            // Add a emoji group that shows all available emojis.
            ...groupEntries
        ]);
        const navigationView = new EmojisNavigationView(locale, emojisGroups);
        const gridView = new EmojiGridView(locale);
        const infoView = new EmojiInfoView(locale);
        gridView.delegate('execute').to(dropdownView);
        gridView.on('tileHover', (evt, data) => {
            infoView.set(data);
        });
        gridView.on('tileFocus', (evt, data) => {
            infoView.set(data);
        });
        // Update the grid of emojis when a user changed the emoji group.
        navigationView.on('execute', () => {
            this._updateGrid(navigationView.currentGroupName, gridView);
        });
        // Set the initial content of the emojis grid.
        this._updateGrid(navigationView.currentGroupName, gridView);
        return { navigationView, gridView, infoView };
    }
}
